import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import DOMPurify from 'dompurify';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ============= Sanitization and Validation Utilities =============

export function sanitizeInput(input: string): string {
  if (!input) return '';
  
  // Remove HTML/scripts
  const cleaned = DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
  
  // Remove common SQL keywords
  const sqlKeywords = ['DROP', 'DELETE', 'INSERT', 'UPDATE', 'SELECT', '--', ';', 'OR 1=1', 'UNION'];
  let result = cleaned;
  sqlKeywords.forEach(keyword => {
    result = result.replace(new RegExp(keyword, 'gi'), '');
  });
  
  return result.trim();
}

export function validateEmail(email: string): { isValid: boolean; message: string } {
  const trimmed = email.trim().toLowerCase();
  
  if (!trimmed) {
    return { isValid: false, message: 'Email é obrigatório' };
  }
  
  // Regex rigorosa para email
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmed)) {
    return { isValid: false, message: 'Email inválido' };
  }
  
  // Bloquear domínios temporários conhecidos
  const tempDomains = ['10minutemail', 'tempmail', 'guerrillamail', 'throwaway'];
  const domain = trimmed.split('@')[1];
  if (tempDomains.some(temp => domain.includes(temp))) {
    return { isValid: false, message: 'Use um email permanente' };
  }
  
  return { isValid: true, message: 'Email válido' };
}

export function validateFullName(name: string): { isValid: boolean; message: string } {
  const trimmed = sanitizeInput(name);
  
  if (trimmed.length < 3) {
    return { isValid: false, message: 'Nome muito curto' };
  }
  
  const words = trimmed.split(' ').filter(w => w.length > 0);
  if (words.length < 2) {
    return { isValid: false, message: 'Digite nome e sobrenome' };
  }
  
  if (/\d/.test(trimmed)) {
    return { isValid: false, message: 'Nome não pode conter números' };
  }
  
  return { isValid: true, message: 'Nome válido' };
}

export function validatePassword(password: string): { isValid: boolean; message: string } {
  if (password.length < 8) {
    return { isValid: false, message: 'Senha deve ter no mínimo 8 caracteres' };
  }
  
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Senha deve conter pelo menos 1 letra maiúscula' };
  }
  
  if (!/[a-z]/.test(password)) {
    return { isValid: false, message: 'Senha deve conter pelo menos 1 letra minúscula' };
  }
  
  if (!/\d/.test(password)) {
    return { isValid: false, message: 'Senha deve conter pelo menos 1 número' };
  }
  
  if (!/[^a-zA-Z0-9]/.test(password)) {
    return { isValid: false, message: 'Senha deve conter pelo menos 1 caractere especial' };
  }
  
  return { isValid: true, message: 'Senha forte' };
}

export function validateDescription(text: string): string {
  const cleaned = sanitizeInput(text);
  
  // Remove palavras inadequadas (expandir conforme necessário)
  const inappropriateWords = ['palavra1', 'palavra2']; // Adicionar conforme necessário
  let result = cleaned;
  inappropriateWords.forEach(word => {
    result = result.replace(new RegExp(word, 'gi'), '***');
  });
  
  return result;
}

// Phone number utilities
export function removePhoneMask(phone: string): string {
  return phone.replace(/\D/g, '');
}

export function formatPhoneNumber(phone: string): string {
  const cleaned = removePhoneMask(phone);
  
  if (cleaned.length === 10) {
    // Format as (XX) XXXX-XXXX for landline
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 11) {
    // Format as (XX) XXXXX-XXXX for mobile
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 9) {
    // Partial mobile without area code
    return cleaned.replace(/(\d{5})(\d{4})/, '$1-$2');
  } else if (cleaned.length === 8) {
    // Partial landline without area code
    return cleaned.replace(/(\d{4})(\d{4})/, '$1-$2');
  } else if (cleaned.length > 2) {
    // Partial with area code
    if (cleaned.length <= 6) {
      return cleaned.replace(/(\d{2})(\d+)/, '($1) $2');
    } else if (cleaned.length <= 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d+)/, '($1) $2-$3');
    } else {
      return cleaned.replace(/(\d{2})(\d{5})(\d+)/, '($1) $2-$3');
    }
  } else if (cleaned.length > 0) {
    // Just area code
    return cleaned.replace(/(\d+)/, '($1');
  }
  
  return cleaned;
}

export function isValidPhone(phone: string): boolean {
  const cleaned = removePhoneMask(phone);
  if (cleaned.length < 10 || cleaned.length > 11) return false;
  
  // Verificar DDD válido (11-99)
  const ddd = parseInt(cleaned.substring(0, 2));
  if (ddd < 11 || ddd > 99) return false;
  
  // Bloquear sequências inválidas
  if (/^(\d)\1+$/.test(cleaned)) return false;
  
  return true;
}

// Display phone number with proper Brazilian formatting
export function displayPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';
  
  const cleaned = removePhoneMask(phone);
  return formatPhoneNumber(cleaned);
}

// CPF/CNPJ utilities
export function removeCpfCnpjMask(cpfCnpj: string): string {
  return cpfCnpj.replace(/\D/g, '');
}

export function formatCpfCnpj(cpfCnpj: string): string {
  const cleaned = removeCpfCnpjMask(cpfCnpj);
  
  if (cleaned.length <= 11) {
    // CPF format: XXX.XXX.XXX-XX
    return cleaned
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2');
  } else {
    // CNPJ format: XX.XXX.XXX/XXXX-XX
    return cleaned
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})/, '$1-$2');
  }
}

export function isValidCpf(cpf: string): boolean {
  const cleaned = removeCpfCnpjMask(cpf);
  
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cleaned)) return false; // Invalid sequences like 11111111111
  
  let sum = 0;
  let remainder;
  
  // First verification digit
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleaned.substring(i - 1, i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned.substring(9, 10))) return false;
  
  // Second verification digit
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleaned.substring(i - 1, i)) * (12 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned.substring(10, 11))) return false;
  
  return true;
}

export function isValidCnpj(cnpj: string): boolean {
  const cleaned = removeCpfCnpjMask(cnpj);
  
  if (cleaned.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cleaned)) return false; // Invalid sequences like 11111111111111
  
  let length = cleaned.length - 2;
  let numbers = cleaned.substring(0, length);
  const digits = cleaned.substring(length);
  let sum = 0;
  let pos = length - 7;
  
  // First verification digit
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  
  // Second verification digit
  length = length + 1;
  numbers = cleaned.substring(0, length);
  sum = 0;
  pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  
  return true;
}

export function isValidCpfCnpj(cpfCnpj: string): boolean {
  const cleaned = removeCpfCnpjMask(cpfCnpj);
  
  if (cleaned.length === 11) {
    return isValidCpf(cpfCnpj);
  } else if (cleaned.length === 14) {
    return isValidCnpj(cpfCnpj);
  }
  
  return false;
}

// Display CPF/CNPJ with proper Brazilian formatting
export function displayCpfCnpj(cpfCnpj: string | null | undefined): string {
  if (!cpfCnpj) return '';
  
  const cleaned = removeCpfCnpjMask(cpfCnpj);
  return formatCpfCnpj(cleaned);
}
